<template>
  <div class="mark-complete d-flex w-100">
    <v-button
      v-if="rootId"
      secondary
      class="p-0 mark-complete__list-button"
      @click="openCompletablePages"
    >
      <v-icon icon="list_alt" size="1.4rem" />
    </v-button>

    <template v-if="isCompleted" class="w-100">
      <v-button
        secondary
        class="d-flex align-items-center justify-content-center pr-2"
        :is-loading="isLoading"
      >
        <div class="flex-grow-1 text-center">
          Completed
          <v-icon icon="done_all" class="done ml-1" />
        </div>
        <v-button
          data-cy="uncomplete"
          block
          x-small
          round
          light
          class="p-0 close"
          @click="uncomplete"
        >
          <v-icon size="14px" icon="close" class="font-weight-bold" />
        </v-button>
      </v-button>
    </template>

    <template v-else class="w-100">
      <v-button
        primary
        @click="handleClick"
        :is-loading="isLoading || !hasCheckedAuthentication"
      >
        Mark Complete
        <v-icon class="ml-1" icon="done_all" />
      </v-button>
    </template>
    <v-button
      v-if="rootId && !isLastPageSequence"
      :secondary="!isCompleted"
      class="p-0 mark-complete__next-button"
      @click="gotToNextPage"
    >
      <v-icon icon="arrow_forward" />
    </v-button>

    <alert-dialog
      :actions="[
        {
          title: 'Close',
          callback: () => (this.showCompleteDialog = false)
        },
        {
          title: 'Continue',
          callback: handleContinue
        }
      ]"
      :show="showCompleteDialog"
      @close="() => (this.showCompleteDialog = false)"
      centered
    >
      <v-icon icon="verified_user" size="4rem" color="var(--brand-color-hex)" />
      <p class="font-weight-bold">
        {{ `${pageTitle} Complete` }}
      </p>
    </alert-dialog>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions, mapGetters } from "vuex"
import EventBus, { EVENTS } from "@/lib/event-bus"
import { BLOCK_ACTIONS } from "@/lib/stream-utils"
import { isPresent } from "@/lib/utils"
import HandleNextPage from "@/lib/mixins/handle-next-page"

export default {
  mixins: [HandleNextPage],

  components: {
    AlertDialog: () => import("@/components/AlertDialog")
  },

  props: {
    pageId: { type: [String, Number], required: true },
    rootId: { type: [String, Number], default: null },
    blockAction: { type: [Object], required: false },
    pageTitle: { type: String, default: "" }
  },

  data() {
    return {
      isLoading: false,
      showCompleteDialog: false
    }
  },

  watch: {
    hasCheckedAuthentication: {
      immediate: true,
      handler: function() {
        if (!this.rootId) return
        if (!this.hasCheckedAuthentication) return
        if (isPresent(this.getPageSequence(this.rootId))) return

        this.isLoading = true
        this.fetchPageSubscribers(this.rootId).finally(
          () => (this.isLoading = false)
        )
      }
    }
  },

  methods: {
    isPresent,

    ...mapMutations("pages", ["setPageSequences", "setPageProgress"]),
    ...mapMutations("tabbar", ["toggleSidebar"]),
    ...mapActions("pages", [
      "completePage",
      "uncompletePage",
      "fetchPageSubscribers"
    ]),

    triggerForm() {
      if (this.blockAction && this.blockAction.form_id) {
        EventBus.$emit(EVENTS.TRIGGER_FORM, {
          formId: this.blockAction.form_id,
          continueAction: this.blockAction.secondary_action
        })
      }
    },

    handleClick() {
      if (this.isAuthenticated) {
        this.isLoading = true

        this.completePage(this.pageId).then(() => {
          // TODO: specs
          if (this.blockAction) {
            const { overlay_id, action } = this.blockAction

            switch (action) {
              case BLOCK_ACTIONS.TRIGGER_FORM:
                this.triggerForm()
                break
              case BLOCK_ACTIONS.TRIGGER_OVERLAY:
                EventBus.$emit(EVENTS.TRIGGER_OVERLAY, overlay_id)
                break
              default:
                this.showCompleteDialog = true
            }
          } else {
            this.showCompleteDialog = true
          }

          this.updateSequence(this.pageId, true)

          this.isLoading = false
        })
      } else {
        this.$router.push("#auth")
        EventBus.$once(EVENTS.POST_AUTH_FLOW, this.triggerForm)
        EventBus.$once(EVENTS.AUTH_ABORTED, () => {
          EventBus.$off(EVENTS.POST_AUTH_FLOW, this.triggerForm)
        })
      }
    },

    openCompletablePages() {
      this.toggleSidebar()
    },

    gotToNextPage() {
      if (this.isAuthenticated) {
        this.handleNextPage(true)
      } else {
        this.$router.push("#auth")
      }
    },

    handleContinue() {
      this.showCompleteDialog = false

      if (this.isLastPageSequence) {
        this.$router.push(this.sequence.homePage.path)

        return
      }

      this.gotToNextPage()
    },

    uncomplete() {
      this.isLoading = true

      this.uncompletePage(this.pageId).then(() => {
        this.updateSequence(this.pageId, false)
        this.isLoading = false
      })
    },

    updateSequence(pageId, complete) {
      const newListSequence = [...this.sequenceList]

      let pageToUpdate = newListSequence.find(page => page.page_id == pageId)

      if (!pageToUpdate) return

      pageToUpdate.is_completed = complete

      this.setPageSequences({
        pageId: this.rootId,
        data: {
          list: newListSequence
        }
      })

      const newCompleteRate =
        newListSequence.length !== 0
          ? newListSequence.filter(page => page.is_completed).length /
            newListSequence.length
          : 0

      this.setPageProgress({
        pageId: this.rootId,
        progress: {
          percent_complete: Math.round(newCompleteRate * 100),
          has_completed: newCompleteRate === 1
        }
      })
    }
  },

  computed: {
    ...mapState("pages", ["pageCompletions"]),
    ...mapGetters("pages", [
      "isPageComplete",
      "getPageSequence",
      "getPageProgress"
    ]),
    ...mapGetters("auth", ["isAuthenticated", "hasCheckedAuthentication"]),

    isCompleted() {
      return this.isPageComplete(this.pageId)
    },

    isLastPageSequence() {
      return (
        this.sequenceList.length > 0 &&
        this.sequenceList.at(-1).page_id === this.pageId
      )
    },

    sequence() {
      return this.getPageSequence(this.rootId)
    },

    sequenceList() {
      return isPresent(this.sequence)
        ? this.sequence.list.filter(page => page.is_completable)
        : []
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.mark-complete {
  &__list-button,
  &__next-button {
    flex-grow: 0 !important;
    flex-shrink: 0;
    width: 54px;
    height: 54px;
  }

  &__list-button {
    margin-right: 0.25rem;
  }

  &__next-button {
    margin-left: 0.25rem;
  }

  .close {
    background-color: $color-ui--grey-40;
    color: $color-ui--grey-20;
  }

  .done {
    color: $color-ui--green-dot;
  }
}
</style>
